import React, {
  ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { Modal } from 'react-bootstrap';
import axios from 'axios';
import Swal from 'sweetalert2';
import { isEqual } from 'lodash';
import { reportValidator } from '../../forms/validators/form-validators';
import { FORM_SUBMISSIONS_REPORTS_BROWSER_API_PREFIX } from '../../../utils/constants';
import { Media } from '../../../structures/models/media/media';
import ReportForm from '../../forms/ReportForm';
import AlertSVG from '../../svgComponents/AlertSVG';

interface Props {
  reportableData: {
    reportableName: string,
    reportableType: string,
    reportableId: number,
  }
}

const ReportFormModal: React.FC<Props> = ({ reportableData }): ReactElement => {
  const [formValues, setFormValues] = useState<{
    name: string,
    email: string,
    details: string,
    images: Array<Media>,
  }>({
    name: '',
    email: '',
    details: '',
    images: [],
  });
  const [loadingError, setLoadingError] = useState('');
  const [loading, setLoading] = useState(false);
  const [cloudinaryIsUpdating, setCloudinaryIsUpdating] = useState(false);
  const [show, setShow] = useState(false);

  const {
    reportableName,
    reportableId,
    reportableType,
  } = reportableData;

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  useEffect(() => {
    if (!reportableName || !reportableId || !reportableType) {
      if (!loadingError) {
        setLoadingError('Could not load report form; check your connection and try again.');
      }
    } else if (loadingError) {
      setLoadingError('');
    }
  }, [reportableName, reportableId, reportableType, loadingError]);

  const updateFormState = useCallback((newValues: object) => {
    const newFormState = {
      ...formValues,
      ...newValues,
    };
    if (!isEqual(newFormState, formValues)) {
      setFormValues(newFormState);
    }
  }, [formValues]);

  const onSubmit = useCallback(async (values) => {
    updateFormState(values);
    setLoading(true);

    const submitValues = {
      ...values,
    };

    // Only allow this on backend for admins
    if (submitValues.acknowledged) {
      delete submitValues.acknowledged;
    }

    axios.post(
      `${FORM_SUBMISSIONS_REPORTS_BROWSER_API_PREFIX}/${reportableType}/${reportableId}`,
      submitValues,
      { headers: { Accept: 'application/json' } },
    )
      .then((response) => {
        if (response.data?.report || (response.status >= 200 && response.status <= 299)) {
          Swal.fire(
            'Success',
            'Report submitted successfully!',
            'success',
          ).then(() => {
            const newFormValues = {
              name: '',
              email: '',
              details: '',
              images: [],
            };
            setFormValues(newFormValues);
            setLoading(false);
            handleClose();
          });
        }
      })
      .catch((e) => {
        // console.log(e);
        setLoading(false);
        Swal.fire(
          'Error',
          e?.message || 'Something went wrong; check your address and connection, then try again.',
          'error',
        );
      });
  }, [formValues, reportableType, reportableId]);

  return (
    <div className="text-center">
      <button
        className="basic-family blue-txt border-secondary btn btn-light mx-auto px-4 py-1 rounded-pill text-uppercase"
        onClick={handleShow}
      >
        <span className="d-flex just-content-center align-items-center">
          <AlertSVG />
          <span>
            {`Report this ${reportableType === 'seller' ? 'seller' : 'ad'}`}
          </span>
        </span>
      </button>
      <Modal
        show={show}
        onHide={handleClose}
        dialogClassName="modal-lg"
        backdropClassName="bg-blue-200"
        scrollable={true}
      >
        <Modal.Body className="p-0">
          {show && (
            <>
              {loadingError
                ? (
                  <div className="alert alert-danger">
                    <p>{loadingError}</p>
                  </div>
                ) : (
                  <ReportForm
                    formTitle={`Report the ${reportableType === 'seller' ? 'seller' : 'listing'}: ${reportableName}`}
                    validator={reportValidator}
                    formValues={formValues}
                    loading={loading}
                    onSubmit={onSubmit}
                    handleClose={handleClose}
                    cloudinaryIsUpdating={cloudinaryIsUpdating}
                    setCloudinaryIsUpdating={setCloudinaryIsUpdating}
                  />
                )}
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ReportFormModal;

if (document.getElementById('react-report-form-modal')) {
  const element = document.getElementById('react-report-form-modal');
  const reportableData = element?.dataset.reportable
    ? JSON.parse(element.dataset.reportable)
    : {
      reportableName: '',
      reportableId: 0,
      reportableType: '',
    };
  ReactDOM.render(
    <ReportFormModal reportableData={reportableData} />,
    document.getElementById('react-report-form-modal'),
  );
}
